import React from 'react';
import scrollIntoView from 'dom-scroll-into-view';
import classnames from 'classnames';
import AuthStore from 'stores/AuthStore';
import UserStore from 'stores/UserStore';
import UserActions from 'actions/UserActions';
import {List, Map, Set} from 'immutable';
import PropTypes from 'prop-types';
import CoachingReview from 'records/CoachingReview';
import connectToStores from 'alt-utils/lib/connectToStores';
import CallStore from 'stores/files/CallStore';
import CoachingReviewStore from 'stores/coaching/CoachingReviewStore';
import PlaylistCall from 'components/coaching/PlaylistCall';
import loadDataForReview from 'actions/coaching/loadDataForReview';
import './PlaylistAddCallsMode.styl';
import UpcomingReviewsStore from 'stores/coaching/UpcomingReviewsStore';
import CoachingTodo from 'records/CoachingTodo';
import PlaylistCallHint from 'components/coaching/PlaylistCallHint';
import CoachingPlan from 'records/CoachingPlan';
import CallActions from 'actions/CallActions';
import UpcomingReviewsActions from 'actions/coaching/UpcomingReviewsActions';
import reviewRole from 'components/pure/utils/reviewRole';
import FileUploadScorecardStore from 'stores/scorecards/FileUploadScorecardStore';
import User from 'records/User';
import Loading from 'components/pure/utils/Loading';
import PlaylistQuestions from 'components/coaching/PlaylistQuestions';
import CoachingReviewTypeSource from 'sources/coaching/CoachingReviewTypeSource';
import CoachingPlaylistActions from 'actions/coaching/CoachingPlaylistActions';
import PlaylistLiveCoachingHint from 'components/coaching/PlaylistLiveCoachingHint';
import PlaylistLiveCoachingStub from 'components/coaching/PlaylistLiveCoachingStub';
import range from 'lodash/range';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import PlaylistPreviewMeetingHint from 'components/coaching/PlaylistPreviewMeetingHint';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';
import CallCardStore from '@store/call-card/CallCardStore';

@connectToStores
export default class PlaylistAddCallsMode extends React.PureComponent {
  callRefs = {};
  state = {
    deleteCall: null,
    newCall: null,
    reviewTypeLoading: true,
    reviewType: null,
  };

  static defaultProps = {
    calls: List(),
    todo: new CoachingTodo({callsToAdd: 0}),
    otherTodo: new CoachingTodo({callsToAdd: 0}),
    reviewFileUploads: List(),
    coachingReviewAnswers: List(),
    userMap: Map(),
  };

  static getStores() {
    return [
      CallStore,
      CoachingReviewStore,
      AuthStore,
      UpcomingReviewsStore,
      CallCardStore,
      FileUploadScorecardStore,
      UserStore,
    ];
  }

  static getPropsFromStores({review}) {
    if (!review) return {};
    const sessionUser = AuthStore.getState().user;
    const callStore = CallStore.getState();
    const reviewStore = CoachingReviewStore.getState();
    const upcomingReviewsMap = UpcomingReviewsStore.getState().upcomingReviewsMap;
    const fileUploadScorecardStore = FileUploadScorecardStore.getState();
    const userStore = UserStore.getState();

    const reviewFileUploads = reviewStore.reviewFileUploads.get(review.id, List());
    const coachingReviewAnswers = reviewStore.coachingReviewAnswers.get(review.id, List());
    const {todo, otherParticipantTodo} = upcomingReviewsMap
      .get(sessionUser.id, Map())
      .get(review.id, {});
    const calls = reviewFileUploads
      .map(f => callStore.partiallyHydratedCalls.get(f.fileuploadId))
      .filter(x => x);

    return {
      todo,
      otherTodo: otherParticipantTodo,
      calls,
      reviewFileUploads,
      coachingReviewAnswers,
      sessionUser,
      scorecardMap: fileUploadScorecardStore.get('scorecards'),
      commentMap: CallCardStore.getState().comments,
      userMap: userStore.users,
      partiallyHydratedCalls: callStore.partiallyHydratedCalls,
    };
  }

  componentDidMount() {
    const {plan, review} = this.props;

    UserActions.fetchAll.defer({
      ids: [plan.recipient.id],
    });
    setTimeout(() => {
      loadDataForReview(review, CallStore.getState()).catch(console.error);
    });
    this.fetchReviewType(review);
    this.fetchUpcomingReviews();
  }

  componentDidUpdate(prevProps, prevState) {
    const {reviewFileUploads, calls} = this.props;

    if (calls.size > prevProps.calls.size) {
      // new call was fetched, scroll it into view
      const oldCalls = Set(prevProps.calls.map(c => c.id));
      const newCall = calls.find(c => !oldCalls.has(c.id));
      if (newCall) {
        scrollIntoView(this.callRefs[newCall.id], this.playlistContainer, {
          offsetBottom: 200,
        });
        // Animate the new call, then remove the class once its finished
        this.setState({newCall: newCall.id}, () => {
          setTimeout(() => this.setState({newCall: null}), 500);
        });
      }
    }

    // A call was added, update our todos and partially hydrated calls
    if (reviewFileUploads.size !== prevProps.reviewFileUploads.size) {
      return this.refreshReviewData();
    }

    // A new comment or scorecard was added, or a question was answered => update todos
    if (this.shouldUpdateTodos(prevProps)) {
      return this.fetchUpcomingReviews();
    }

    // Done loading review type.
    if (!this.state.reviewTypeLoading && prevState.reviewTypeLoading) {
      const {reviewType} = this.state;
      // Enable the "add to plan" button on the calls page.
      CoachingPlaylistActions.setCanAddCalls(reviewType && reviewType.includesCalls);
    }
  }

  /**
   * Checks whether the number of comments or scorecards for any of the given calls has changed
   */
  shouldUpdateTodos(prevProps) {
    const {calls, commentMap, scorecardMap, coachingReviewAnswers} = this.props;

    if (prevProps.coachingReviewAnswers.size !== coachingReviewAnswers.size) {
      return true;
    }

    return calls.some(call => {
      const comments = commentMap.get(call.id, List());
      const prevComments = prevProps.commentMap.get(call.id, List());

      const scorecards = scorecardMap.get(call.id, List()).map(s => s.results.size);
      const prevScorecards = prevProps.scorecardMap.get(call.id, List()).map(s => s.results.size);
      return !comments.equals(prevComments) || !scorecards.equals(prevScorecards);
    });
  }

  fetchReviewType(review) {
    if (!review || !review.reviewTypeId) {
      // Nothing to load. Never will be.
      this.setState({reviewTypeLoading: false});
    }
    this.setState({reviewTypeLoading: true}, async () => {
      const reviewTypes = await CoachingReviewTypeSource.fetchByIds([review.reviewTypeId]);
      const newState = {
        reviewTypeLoading: false,
      };
      // will get either one or none.
      if (reviewTypes.length === 1) {
        newState['reviewType'] = reviewTypes[0];
      }
      this.setState(newState);
    });
  }

  refreshReviewData() {
    const {calls, reviewFileUploads, partiallyHydratedCalls, review} = this.props;

    const unpulledCalls = reviewFileUploads
      .map(r => r.fileuploadId)
      .filter(id => !partiallyHydratedCalls.get(id));
    if (!unpulledCalls.isEmpty()) {
      CallActions.getPartiallyHydratedCalls.defer(unpulledCalls);
    }

    this.fetchUpcomingReviews();
    UserActions.fetchAll.defer({
      ids: calls.map(c => c.callerId).concat(review.coachId),
    });
  }

  fetchUpcomingReviews() {
    const {sessionUser, review} = this.props;

    UpcomingReviewsActions.upcomingReviews.defer(
      sessionUser.id,
      reviewRole(review, sessionUser.id),
      undefined,
      undefined,
      review.id
    );
  }

  render() {
    const {
      calls,
      todo,
      otherTodo,
      sessionUser,
      plan,
      focusedCallId,
      userMap,
      review,
      onCallsTab,
      fromPlanSubPage,
    } = this.props;

    const {reviewTypeLoading, reviewType, newCall, deleteCall} = this.state;

    // Check for alternative review-specific coach
    const coachInfo = userMap.get(review.coachUserId, plan.coach.info);
    const coachParticipant = plan.coach.set('info', coachInfo);
    const isCoach = sessionUser.id === review.coachUserId;

    const [participant, otherParticipant] =
      sessionUser && sessionUser.id === plan.recipient.id
        ? [plan.recipient, coachParticipant]
        : [coachParticipant, plan.recipient];

    if (reviewTypeLoading || calls.some(c => !userMap.get(c.callerId))) {
      return <Loading />;
    }
    const noCallsAllowed = reviewType && !reviewType.includesCalls;
    const callsAllowed = !noCallsAllowed;

    return (
      <div
        ref={r => (this.playlistContainer = r)}
        className="playlist-add-calls-mode playlist-content">
        <PlaylistQuestions
          rep={plan.recipient}
          review={review}
          reviewType={reviewType}
          sessionUser={sessionUser}
          todo={todo}
        />
        {noCallsAllowed ||
          calls.map((call, i) => (
            <div
              className={classnames({
                addCall: newCall === call.id,
                deleteCall: deleteCall === call.id,
              })}
              key={'call' + i}
              ref={r => (this.callRefs[call.id] = r)}>
              <PlaylistCall
                active={call.id === focusedCallId}
                todo={todo}
                otherTodo={otherTodo}
                call={call}
                caller={userMap.get(call.callerId)}
                index={i}
                participant={participant}
                otherParticipant={otherParticipant}
                onDelete={() => {
                  this.setState({
                    deleteCall: call.id,
                  });
                  // Delay the removal until the animation is finished
                  setTimeout(() => {
                    CoachingReviewActions.deleteReviewFileUpload(review.id, call.id);
                    this.setState({deleteCall: null});
                  }, 500);
                }}
                reviewId={review.id}
              />
            </div>
          ))}
        {isCoach &&
          callsAllowed &&
          range(calls.size, calls.size + review.livecoachingStubs).map(callIndex => (
            <div
              key={callIndex}
              className={classnames({
                addCall:
                  newCall === 'live' && callIndex === calls.size + review.livecoachingStubs - 1,
                deleteCall:
                  deleteCall === 'live' && callIndex === calls.size + review.livecoachingStubs - 1,
              })}>
              <PlaylistLiveCoachingStub
                onDelete={() => {
                  this.setState({deleteCall: 'live'});
                  setTimeout(() => {
                    CoachingReviewActions.setLiveCoachingStubs(
                      review.id,
                      review.livecoachingStubs - 1
                    ).then(() => {
                      this.setState({deleteCall: null});
                      UpcomingReviewsActions.upcomingReviews(
                        sessionUser.id,
                        UPCOMING_REVIEW_ROLES.COACH,
                        undefined,
                        1,
                        review.id
                      );
                    });
                  }, 300);
                }}
                key={callIndex}
                index={callIndex}
                review={review}
                plan={plan}
                sessionUser={sessionUser}
              />
            </div>
          ))}
        {callsAllowed && (
          <PlaylistCallHint
            onCallsTab={onCallsTab}
            plan={plan}
            review={review}
            sessionUser={sessionUser}
            todo={todo}
          />
        )}
        {isCoach && (
          <PlaylistLiveCoachingHint
            review={review}
            sessionUser={sessionUser}
            todo={todo}
            onAdd={() => {
              this.setState({newCall: 'live'});
              setTimeout(() => {
                this.setState({newCall: null});
              }, 300);
            }}
          />
        )}
        {isCoach && !fromPlanSubPage && todo?.callsToAdd === 0 && (
          <PlaylistPreviewMeetingHint rep={plan.recipient} review={review} todo={todo} />
        )}
      </div>
    );
  }
}

PlaylistAddCallsMode.propTypes = {
  plan: PropTypes.instanceOf(CoachingPlan),
  review: PropTypes.instanceOf(CoachingReview),
  todo: PropTypes.instanceOf(CoachingTodo),
  sessionUser: PropTypes.instanceOf(User),
  focusedCallId: PropTypes.string, // FileUploadId
  calls: PropTypes.instanceOf(List), // CallDetails
  reviewFileUploads: PropTypes.instanceOf(List), // CoachingReviewFileUploads,
  scorecardMap: PropTypes.instanceOf(Map), // Map<FileUploadId, Scorecard>
  commentMap: PropTypes.instanceOf(Map), // Map<FileUploadId, number>
  userMap: PropTypes.instanceOf(Map), // Map<UserId, User>
  partiallyHydratedCalls: PropTypes.instanceOf(Map), // Map<FileUploadId, CallDetails>
};
